import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Send,
  Plus,
  ChevronRight,
  Users,
  MessageSquare,
  FileSignature,
  ExternalLink,
} from 'lucide-react'
import { useCreateNegotiation } from '../../hooks/useNegotiations'
import { useOfficeDocument } from '../../hooks/useOfficeDocument'
import { InlineHint } from '../common/InlineHint'
import { ContextualTip } from '../common/ContextualTip'
import { PartyRow } from './PartyRow'
import type { PartyInput } from './PartyRow'
import { MAIN_APP_URL } from '../../config'

const EMPTY_PARTY: PartyInput = { label: '', tagName: '', role: 'CUSTOMER' }

interface SendNegotiationCreateProps {
  actorId?: string
  title: string
  onTitleChange: (title: string) => void
  showToast?: (type: 'success' | 'error' | 'info', message: string) => void
  onCreateSuccess: (negotiationId: number, version: number) => void
  onSending: (message: string) => void
  onError: (message: string) => void
}

export function SendNegotiationCreate({
  actorId,
  title,
  onTitleChange,
  showToast,
  onCreateSuccess,
  onSending,
  onError,
}: SendNegotiationCreateProps) {
  const { t } = useTranslation()
  const { getDocumentBlob, isReading } = useOfficeDocument()
  const createNegotiation = useCreateNegotiation()

  // Shared state
  const [deadline, setDeadline] = useState('')
  const [parties, setParties] = useState<PartyInput[]>([{ ...EMPTY_PARTY }])
  const [error, setError] = useState('')

  // Party management
  const handlePartyChange = useCallback((index: number, party: PartyInput) => {
    setParties((prev) => prev.map((p, i) => (i === index ? party : p)))
  }, [])

  const handlePartyRemove = useCallback((index: number) => {
    setParties((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleAddParty = useCallback(() => {
    setParties((prev) => [...prev, { ...EMPTY_PARTY }])
  }, [])

  // Validation
  const existingTags = parties.map((p) => p.tagName).filter(Boolean)
  const validParties = parties.filter((p) => (p.actorId || p.email) && p.label.trim() && p.tagName.trim())
  const hasInvalidTags = parties.some((p) => {
    if (!p.tagName) return false
    const lower = p.tagName.toLowerCase()
    return lower === 'intern' || lower === 'alle' || !/^[a-zA-Z0-9_]{1,15}$/.test(p.tagName)
  })
  const hasDuplicateTags = new Set(existingTags.map((t) => t.toLowerCase())).size !== existingTags.length

  const canSend = title.trim() && validParties.length > 0 && !hasInvalidTags && !hasDuplicateTags
    && !createNegotiation.isPending && !isReading

  // Create from current document (blank flow)
  const handleCreate = async () => {
    setError('')
    onSending(t('negotiate.uploadingDocument'))

    try {
      const blob = await getDocumentBlob()
      const filename = `${title.trim()}.docx`

      onSending(t('negotiate.creatingNegotiation'))

      const result = await createNegotiation.mutateAsync({
        file: blob,
        filename,
        title: title.trim(),
        parties: validParties,
        deadline: deadline || undefined,
        actorId,
        getDocumentBlob,
      })

      onCreateSuccess(result.negotiation.id, result.version.version_number)
      showToast?.('success', t('negotiate.negotiationSent'))
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err)
      // RBAC 403 — show a clear permission message instead of generic error
      const msg = raw.includes('403')
        ? t('negotiate.errors.forbidden', 'You don\'t have permission to create negotiations in this organization. Contact your administrator.')
        : raw
      setError(msg)
      onError(msg)
      showToast?.('error', msg)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="px-3 py-3 space-y-3">
        <InlineHint hintKey="negotiation-create-intro">
          <p className="text-[10px] text-neutral-500 dark:text-neutral-400">
            {t('negotiate.hint')}
          </p>
        </InlineHint>

        {/* 3-step onboarding visual */}
        <div className="flex items-center justify-between gap-1 px-2 py-2 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-100 dark:border-neutral-800">
          <div className="flex flex-col items-center gap-1 flex-1">
            <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Users size={14} className="text-primary-600 dark:text-primary-400" />
            </div>
            <span className="text-[9px] font-medium text-neutral-600 dark:text-neutral-400 text-center leading-tight">
              {t('negotiate.onboardingStep1')}
            </span>
          </div>
          <ChevronRight size={12} className="text-neutral-300 dark:text-neutral-600 shrink-0" />
          <div className="flex flex-col items-center gap-1 flex-1">
            <div className="w-7 h-7 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <MessageSquare size={14} className="text-neutral-400 dark:text-neutral-500" />
            </div>
            <span className="text-[9px] font-medium text-neutral-400 dark:text-neutral-500 text-center leading-tight">
              {t('negotiate.onboardingStep2')}
            </span>
          </div>
          <ChevronRight size={12} className="text-neutral-300 dark:text-neutral-600 shrink-0" />
          <div className="flex flex-col items-center gap-1 flex-1">
            <div className="w-7 h-7 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <FileSignature size={14} className="text-neutral-400 dark:text-neutral-500" />
            </div>
            <span className="text-[9px] font-medium text-neutral-400 dark:text-neutral-500 text-center leading-tight">
              {t('negotiate.onboardingStep3')}
            </span>
          </div>
        </div>

        <ContextualTip context="first-negotiation">
          {t('tips.firstNegotiation')}
        </ContextualTip>

        {/* Title */}
        <div>
          <label className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
            {t('negotiate.documentTitle')}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={t('negotiate.documentTitlePlaceholder')}
            className="mt-0.5 w-full px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Deadline */}
        <div>
          <label className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
            {t('negotiate.deadline')}
          </label>
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="mt-0.5 w-full px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Parties */}
        <div>
          <label className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1.5 block">
            {t('negotiate.addParty')}
          </label>
          <div className="space-y-2">
            {parties.map((party, index) => (
              <PartyRow
                key={index}
                party={party}
                index={index}
                actorId={actorId}
                existingTags={existingTags}
                onChange={handlePartyChange}
                onRemove={handlePartyRemove}
                canRemove={parties.length > 1}
              />
            ))}
          </div>
          <button
            onClick={handleAddParty}
            className="mt-2 flex items-center gap-1 text-[11px] text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <Plus size={10} />
            {t('negotiate.addParty')}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-2 rounded bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
            <p className="text-xs text-error-600 dark:text-error-400">{error}</p>
          </div>
        )}

        {/* Web app cross-link — reminds users that the full negotiations
            list lives in the browser, not the task pane (Bulk G1). */}
        <a
          href={`${MAIN_APP_URL}/dashboard?view=negotiations`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 pt-1 text-[10px] text-neutral-400 hover:text-primary-500 transition-colors"
        >
          {t('negotiate.viewAllInBrowser')}
          <ExternalLink size={9} />
        </a>
      </div>

      {/* Submit button -- pinned to bottom */}
      <div className="mt-auto px-3 py-3 border-t border-neutral-100 dark:border-neutral-800">
        {!validParties.length && parties.some((p) => p.label) && (
          <p className="text-[10px] text-neutral-400 mb-1.5 text-center">
            {t('negotiate.atLeastOneParty')}
          </p>
        )}
        <button
          onClick={handleCreate}
          disabled={!canSend}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 hover:shadow-primary active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all duration-200"
        >
          <Send size={14} />
          {t('negotiate.sendToNegotiate')}
        </button>
      </div>
    </div>
  )
}
